import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getParentPath } from 'utils/Global';
import {
  InspectionDTO,
  WellImageDTO,
  SaveImageRequestDTO,
  WellDetailsState,
} from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import WellService from 'business/services/WellService';
import { ImageReportOptionsDTO } from './ImageReportOptions';
import { blankImage, fonts, pageSize } from './ReportsConstants';
import {
  createPdfWithPopupBlockerHandler,
  estimateCharsPerLine,
  getFieldRuns,
  getScreenNameLineRuns,
  getScreenNamesFromLayers,
  IMAGE_REPORT_INFO_FONT_SIZE,
  IMAGE_REPORT_INFO_FIELD_SEPARATOR,
  IMAGE_REPORT_INFO_MARGIN,
  PDFTextProps,
} from './utils';
pdfMake.fonts = { Roboto: fonts.Roboto };

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
  paddingLeft: function (i, node) {
    return 0;
  },
  paddingRight: function (i, node) {
    return 0;
  },
  paddingTop: function (i, node) {
    return 0;
  },
  paddingBottom: function (i, node) {
    return 0;
  },
};

function getImageReportHeaderRuns(
  fields: { label: string; value: any }[],
  screenNameRuns: PDFTextProps[],
) {
  return [
    ...getFieldRuns(fields, IMAGE_REPORT_INFO_FIELD_SEPARATOR),
    { text: '\n' },
    ...screenNameRuns,
  ];
}

export async function generateImageReportPDF(
  options: ImageReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: any[],
  selectedDropImageParameters: any,
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  const pageDimensions: any = {
    pageWidth: 842,
    pageHeight: 'auto',
  };

  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Image Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(
      options,
      expData,
      wellDetails,
      selectedDropImageParameters,
      pageDimensions,
    ),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
    },
    pageMargins: [20, 30, 20, 10],
    pageOrientation: options.rowLimit > options.columnLimit ? 'portrait' : 'landscape',
    pageSize: 'A4',
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Image Report',
    saveReport,
    onPopupBlocked,
  );
}

async function CreateContent(
  options: ImageReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: any[],
  selectedDropImageParameters: any,
  pageDimensions: any,
) {
  const content = [];
  if (options.fitToPage === true) {
    options.columnLimit = expData.containerCols;
    options.rowLimit = expData.containerRows;
  }
  const wellInfo = {} as any;
  wellInfo.plateID = wellDetails[wellDetails.length - 1].plateID;
  wellInfo.cpID = selectedDropImageParameters.cpId;
  wellInfo.dropNumber = selectedDropImageParameters.dropNumber;
  const plateInfo: any = expData.plateDto.find(
    (plate) => plate.plateId === wellInfo.plateID,
  );
  const inspection: any = expData.inspections.find((ins) => ins.id == options.inspection);

  const treeService = new TreeService();
  const expParents = await treeService.getDocumentParentNodes(
    expData.experimentId,
    ETreeNodeTypes.Experiment,
    true,
  );
  const expPath = expParents ? getParentPath(expParents) : '';
  const screenNames = getScreenNamesFromLayers(expData.layers);
  const orientation = options.rowLimit > options.columnLimit ? 'portrait' : 'landscape';
  const contentWidth =
    (orientation === 'portrait' ? pageSize.a4.width : pageSize.a4.height) -
    IMAGE_REPORT_INFO_MARGIN * 2;
  const maxLineChars = estimateCharsPerLine(contentWidth, IMAGE_REPORT_INFO_FONT_SIZE);
  const screenNameRuns = getScreenNameLineRuns(screenNames, maxLineChars);
  let dropNumber = 0;

  const plateGroupedInspection: InspectionDTO[] = [];

  expData.inspections
    .find((insp) => insp.id == options.inspection)
    ?.groupedInspections.forEach((gi) => {
      if (gi.plateId === plateInfo.plateId) {
        plateGroupedInspection.push(gi);
      }
    });

  const dateImaged = new Date(inspection.dateImaged);

  if (options.dropNumber === 'currentDrop') {
    dropNumber = wellInfo.dropNumber;

    const data = await getImagesTable(
      expData,
      plateInfo.plateId,
      dropNumber,
      wellInfo.cpID,
      plateGroupedInspection[0].imageBatchId,
    );

    if (data.length > 0) {
      content.push({
        text: getImageReportHeaderRuns(
          [
            { label: 'Experiment: ', value: expPath },
            { label: 'Plate ID: ', value: plateInfo.plateId },
            { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
            { label: 'Drop Number: ', value: dropNumber },
            {
              label: 'Inspection Date: ',
              value:
                dateImaged.toLocaleTimeString() +
                ' ' +
                dateImaged.toLocaleDateString('en-US'),
            },
          ],
          screenNameRuns,
        ),
      });

      content.push(await designTableData(options, data, expData));
    } else {
      content.push({
        text: 'The document does not contain any pages.',
        alignment: 'center',
        fontSize: 12,
        color: 'grey',
      });
    }
  } else if (options.dropNumber === 'allDrops') {
    let isImagesDataAvailable = false;
    for (const drop of expData.containerDrops) {
      const data = await getImagesTable(
        expData,
        plateInfo.plateId,
        drop.dropNumber,
        wellInfo.cpID,
        plateGroupedInspection[0].imageBatchId,
      );

      if (data.length > 0) {
        isImagesDataAvailable = true;
        content.push({
          text: getImageReportHeaderRuns(
            [
              { label: 'Experiment: ', value: expPath },
              { label: 'Plate ID: ', value: plateInfo.plateId },
              { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
              { label: 'Drop Number: ', value: drop.dropNumber },
              {
                label: 'Inspection Date: ',
                value:
                  dateImaged.toLocaleTimeString() +
                  ' ' +
                  dateImaged.toLocaleDateString('en-US'),
              },
            ],
            screenNameRuns,
          ),
          pageBreak: drop.dropNumber == 1 ? '' : 'before',
        });

        content.push(await designTableData(options, data, expData));
      }
    }

    if (!isImagesDataAvailable) {
      content.push({
        text: 'The document does not contain any pages.',
        alignment: 'center',
        fontSize: 12,
        color: 'grey',
      });
    }
  } else {
    dropNumber = Number(options.dropNumber);

    const data = await getImagesTable(
      expData,
      plateInfo.plateId,
      dropNumber,
      wellInfo.cpID,
      plateGroupedInspection[0].imageBatchId,
    );

    if (data.length > 0) {
      content.push({
        text: getImageReportHeaderRuns(
          [
            { label: 'Experiment: ', value: expPath },
            { label: 'Plate ID: ', value: plateInfo.plateId },
            { label: 'Barcode: ', value: plateInfo.plateBarName.split(',')[2] },
            { label: 'Drop Number: ', value: dropNumber },
            {
              label: 'Inspection Date: ',
              value:
                dateImaged.toLocaleTimeString() +
                ' ' +
                dateImaged.toLocaleDateString('en-US'),
            },
          ],
          screenNameRuns,
        ),
      });

      content.push(await designTableData(options, data, expData));
    } else {
      content.push({
        text: 'The document does not contain any pages.',
        alignment: 'center',
        fontSize: 12,
        color: 'grey',
      });
    }
  }

  content.push('\n');
  content.push('\n');
  return content;
}

export async function designTableData(
  options: ImageReportOptionsDTO,
  data: any,
  expData: ExperimentDtoClient,
) {
  const columnLimit = options.columnLimit;
  const rowLimit = options.rowLimit;
  const rows: any[] = [];
  let columns: any[] = [];
  let rowCount = 1;
  let rowStart = 0;
  let columnStart = 1;
  let rowIndex = 0;
  while (rowIndex < expData.containerRows) {
    for (let i = rowStart; i < rowLimit + rowStart; i++) {
      let columnCount = 1;

      for (let j = columnStart; j < columnLimit + columnStart; j++) {
        if (columnCount === 1) {
          if (i < data.length) {
            columns.push({
              index: '',
              data: data[i].columns[0],
            });
          }
        }
        if (i < data.length) {
          columns.push({
            index: j,
            data: data[i].columns[j],
          });
        }
        if (columnCount === columnLimit || j === expData.containerCols) {
          rows.push(columns);
          columns = [];
          columnCount = 0;
        }
        columnCount++;
        if (j === expData.containerCols) {
          break;
        }
      }

      if (rowCount === rowLimit) {
        columnStart = columnStart + columnLimit;
        if (i >= expData.containerRows && columnStart > expData.containerCols) {
          rowIndex = rowStart + rowLimit;
          break;
        }
        if (columnStart > expData.containerCols) {
          columnStart = 1;
          rowStart = rowStart + rowLimit;
          rowIndex = rowStart;
          rowCount = 0;
        } else {
          rowStart = rowIndex;
          rowCount = 0;
        }
      }
      rowCount++;
      if (i === expData.containerRows && columnStart > expData.containerCols) {
        rowIndex = rowStart + rowLimit;
        break;
      }
    }
  }

  let loopCount = 0;
  while (loopCount < rows.length) {
    if (rows[loopCount].length === 0) {
      rows.splice(loopCount, 1);
    } else {
      ++loopCount;
    }
  }

  let lastRows: any[] = [];
  const rowTable: any[] = [];

  let rowNumber = 0;

  for (const row of rows) {
    rowNumber++;
    if (row.length > 0) {
      if (lastRows.length === 0) {
        let columnTop: any[] = [];
        row.forEach((col) => {
          columnTop.push({
            data: [
              {
                text: col.index,
                fontSize: 5,
                alignment: 'center',
                border: [false, false, false, false],
              },
            ],
          });
        });

        lastRows.push(columnTop);
        columnTop = [];
      }

      lastRows.push(row);
      const isFullPage = lastRows.length === options.rowLimit + 1;
      const isLastRow = rowNumber === rows.length;
      if (isFullPage || isLastRow) {
        let pageBreak = 'after';
        if (isLastRow) {
          pageBreak = '';
        }

        rowTable.push(await getFinalTable(lastRows, pageBreak, options));
        lastRows = [];
      }
    }
  }

  return rowTable;
}

async function getFinalTable(
  rows: any[],
  pageBreak: string,
  options: ImageReportOptionsDTO,
) {
  const maxColumns = Math.max(...rows.map((r) => r.length)) - 1;

  const widths = [];
  let tableWidth = 790;
  let tableHeight = 500;

  if (options.rowLimit > options.columnLimit) {
    tableWidth = 540;
    tableHeight = 740;
  }

  widths.push(10);
  const singleColumnWidth =
    (tableWidth - 0.5 * options.columnLimit) / options.columnLimit;

  for (let i = 0; i < maxColumns; i++) {
    widths.push(singleColumnWidth);
  }

  const numberOfRows = rows.length - 1;
  const heights = [];

  heights.push(10);
  const singleRowHeight = (tableHeight - 0.5 * options.rowLimit) / options.rowLimit;
  for (let i = 0; i < numberOfRows; i++) {
    heights.push(singleRowHeight);
  }

  let imageWidth, imageHeight;
  outerLoop: for (let i = 1; i < rows.length; i++) {
    for (let j = 1; j < rows[i].length; j++) {
      if (rows[i][j]?.data?.[0]?.isImageAvailable) {
        imageWidth = rows[i][j].data[0].imageWidth;
        imageHeight = rows[i][j].data[0].imageHeight;
        break outerLoop;
      }
    }
  }

  const countFontSize = 5;
  const aspectRatio = imageHeight / imageWidth;
  const scaledImageHeight = singleColumnWidth * aspectRatio;
  let marginTop = (singleRowHeight - scaledImageHeight) / 2;
  marginTop = marginTop > 0 ? marginTop : 0;
  const alphabetCountColumnMarginTop = (singleRowHeight - countFontSize) / 2;

  const rows1: any[] = [];

  for (const row of rows) {
    const tableColumns: any[] = [];

    const paddedRow = [...row];
    while (paddedRow.length < maxColumns + 1) {
      paddedRow.push({
        index: '',
        data: [{ text: '', border: [false, false, false, false] }],
      });
    }

    for (const col of paddedRow) {
      if (col.index === '') {
        tableColumns.push({
          text: col.data[0].text,
          alignment: 'center',
          border: [false, false, false, false],
          margin: [0, alphabetCountColumnMarginTop, 0, 0],
          fontSize: countFontSize,
        });
      } else {
        const cell: any = { alignment: 'center' };

        if ('text' in col.data[0]) {
          cell.text = col.data[0].text;
          cell.fontSize = countFontSize;
          cell.border = [false, false, false, false];
        }

        if ('image' in col.data[0]) {
          cell.image = col.data[0].image;
          cell.fit = [singleColumnWidth, singleRowHeight];
          cell.margin = [0, marginTop, 0, 0];
        }

        tableColumns.push(cell);
      }
    }

    rows1.push(tableColumns);
  }

  return {
    table: {
      heights: heights,
      widths: widths,
      headerRows: rows1.length,
      body: rows1,
    },
    layout: TABLE_LAYOUT,
    pageBreak,
  };
}

export async function getImagesTable(
  expData: ExperimentDtoClient,
  plateId: number,
  dropNumber: number,
  captureProfileId: number,
  imageBatchId: number,
) {
  const rows: any = [];
  let columns: any = [];

  const dropImages: any[] = await getImages(
    expData,
    plateId,
    dropNumber,
    captureProfileId,
    imageBatchId,
  );

  if (dropImages.length === 0) {
    return rows;
  }

  const sampleImageForDimensions = dropImages.find((image) => image.isImageAvailable);
  const imageDimensions: any = await getImageDimensions(
    sampleImageForDimensions.wellImage,
  );

  let dropImageCount = 0;
  for (let i = 0; i < expData.containerRows; i++) {
    for (let j = 0; j < expData.containerCols + 1; j++) {
      if (j === 0) {
        columns.push([
          {
            text: getAlphabeticCount(i),
            alignment: 'center',
            margin: [0, 0, 0, 0],
            border: [false, false, false, false],
          },
        ]);
      } else if (dropImages[dropImageCount]) {
        columns.push([
          {
            image: dropImages[dropImageCount].wellImage,
            imageWidth: imageDimensions.width,
            imageHeight: imageDimensions.height,
            isImageAvailable: dropImages[dropImageCount].isImageAvailable,
          },
        ]);
        dropImageCount++;
      }
    }
    rows.push({
      alignment: 'center',
      columns: columns,
    });
    columns = [];
  }
  return rows;
}

export async function getImages(
  expData: ExperimentDtoClient,
  plateId: number,
  dropNumber: number,
  captureProfileId: number,
  imageBatchId: number,
) {
  const wellService = new WellService();
  const dropImages: WellImageDTO[] = [];

  const imageDto: SaveImageRequestDTO = new SaveImageRequestDTO();
  imageDto.saveAllRegions = true;
  imageDto.saveAllWellDropScores = true;
  imageDto.saveAnnotations = true;
  imageDto.selectedCaptureProfileIds = [];
  imageDto.selectedCaptureProfileIds.push(captureProfileId);
  imageDto.inspectionSetDTOs = [];
  imageDto.saveAllDrops = false;
  imageDto.saveAllFocusLevels = false;
  imageDto.saveAllInspections = false;
  imageDto.wellDetails = [];

  for (const wl of expData.wellLables) {
    const regionId = getRegionId(
      expData.regions,
      wl.wellNumber,
      dropNumber,
      'Drop',
      plateId,
    );

    const wellInfoDetails = new WellDetailsState();
    wellInfoDetails.cpID = captureProfileId;
    wellInfoDetails.dropNumber = dropNumber;
    wellInfoDetails.imageBatchId = imageBatchId;
    wellInfoDetails.plateID = plateId;
    wellInfoDetails.regionID = regionId;
    wellInfoDetails.regionName = 'Drop';
    wellInfoDetails.wellNum = wl.wellNumber;

    imageDto.wellDetails.push(wellInfoDetails);
  }
  const responseData: any[] = await wellService.getWellImagesForImageReport(imageDto);

  if (responseData.length === 0) {
    return dropImages;
  }

  for (const wl of expData.wellLables) {
    let dropImage: any;
    dropImage = responseData.find((img) => img.wellNumber == wl.wellNumber);
    if (!dropImage?.wellImage) {
      dropImage = new WellImageDTO();
      dropImage.wellImage = blankImage;
      dropImage.score = null;
      dropImage.wellNumber = wl.wellNumber;
      dropImage.isImageAvailable = false;
    }
    dropImages.push(dropImage);
  }
  return dropImages;
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
          alignment: 'right',
        },
      ],
      margin: [20, 5, 20, 20],
    },
  ];
}

export function getAlphabeticCount(n) {
  let result = '';
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

export function getImageDimensions(base64String) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64String;

    img.onload = function () {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      resolve({ width, height });
    };

    img.onerror = function () {
      reject('Failed to load image.');
    };
  });
}
